import { Property, PropertySearchResponseDto, ProposalPropertyMapping } from '../models/property.model';
import { ListItem } from '../models/ListItem';
import GenericService from './genericService';
import { httpService } from './httpService';

class PropertyService extends GenericService<Property, ListItem> {
  constructor() {
    super('/api/Property')
  }

  // Custom: Get properties by city ID
  async getPropertiesByCityId(cityId: number | string): Promise<Property[]> {
    return await httpService.get<Property[]>(`/api/Property/GetByCityId/${cityId}`, undefined, {
      unwrap: true,
      normalize: true,
      toast: false,
    });
  }

  // Custom: Get properties by proposal ID
  async getPropertiesByProposalId(proposalId: number | string): Promise<Property[]> {
    return await httpService.get<Property[]>(`/api/Property/GetByProposalId/${proposalId}`, undefined, {
      unwrap: true,
      normalize: true,
      toast: false,
    });
  }

  async getPropertiesByCityIdAndProposalId(cityId: number | string, proposalId: number | string): Promise<Property[]> {
    return await httpService.get<Property[]>(`/api/Property/GetByCityIdAndProposalId/${cityId}/${proposalId}`, undefined, {
      unwrap: true,
      normalize: true,
      toast: false,
    });
  }
  async getPropertiesByCityIdAndRmcId(cityId: number | string, rmcId: number | string): Promise<Property[]> {
    return await httpService.get<Property[]>(
      `/api/Property/GetByCityIdAndRMCId/${cityId}/${rmcId}`,
      undefined,
      {
        unwrap: true,
        normalize: true,
        toast: false,
      }
    );
  }
  // Custom: Get properties by city ID
  async getByPropertyId(propertyId: number | string): Promise<Property> {
    return await httpService.get<Property>(`/api/Property/GetByPropertyId/${propertyId}`, undefined, {
      unwrap: true,
      normalize: true,
      toast: false,
    });
  }
  // Custom: Get properties by city and client
  async searchProperties(cityId: number | string, checkIn: string, checkOut: string, roomTypeId: number | string): Promise<PropertySearchResponseDto> {
    return await httpService.get<any>(`/api/Property/SearchProperties/${cityId}`, {
      params: {
        checkIn: checkIn,
        checkOut: checkOut,
        roomTypeId: roomTypeId
      }
    }, {
      unwrap: true,
      normalize: true,
      toast: false,
    });
  }
  async searchPropertiesForB2BUser(
    checkIn: string, 
    checkOut: string, 
    roomTypeId: number | string,
    filters?: {
      locationId?: number | string;
      distanceKm?: number;
      propertyTypeId?: number | string;
      categoryId?: number | string;
      cityId?: number | string;
    }
  ): Promise<any> {
    const params: any = {
      checkIn: checkIn,
      checkOut: checkOut,
      roomTypeId: roomTypeId,
    };
    
    // Add optional filter parameters
    if (filters) {
      if (filters.locationId) params.locationId = filters.locationId;
      if (filters.distanceKm) params.distanceKm = filters.distanceKm;
      if (filters.propertyTypeId) params.propertyTypeId = filters.propertyTypeId;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.cityId) params.cityId = filters.cityId;
    }
    
    return await httpService.get<PropertySearchResponseDto>(`/api/Property/SearchPropertiesForB2BUser`, {
      params
    }, {
      unwrap: true,
      normalize: true,
      toast: false,
    });
  }

  // Custom: Get property IDs by client ID
  async getPropertyIdsbyRMCClientMappingId(rmcClientMappingId: number | string, cityId: number | string, onlyRmcClient = false): Promise<number[]> {
    if (onlyRmcClient) {
      onlyRmcClient = true;
    }
    return await httpService.get<number[]>(`/api/Property/GetPropertyIdsbyRMCClientMappingId/${rmcClientMappingId}/${cityId}/${onlyRmcClient}`, undefined, {
      unwrap: true,
      normalize: false,
      toast: false,
    });
  }

  // Custom: Get all properties
  async getAllProperties(): Promise<Property[]> {
    return await httpService.get<Property[]>('/api/Property/GetAllProperties', undefined, {
      unwrap: true,
      normalize: true,
      toast: false,
    });
  }

  // Custom: Create client-property mappings
  async createClientPropertyMappings(rmcClientMappingId: Number, cityId: Number, mappings: ProposalPropertyMapping[]): Promise<boolean> {
    try {
      await httpService.post(
        `/api/Property/CreateClientPropertyMappings/${rmcClientMappingId}/${cityId}`,
        mappings,
        undefined,
        {
          unwrap: true,
          toast: true,
        }
      );
      return true;
    } catch (error) {
      console.error('Failed to create client-property mappings', error);
      return false;
    }
  }
  async createRMCClientPropertyMappings(rmcClientMappingId: Number, cityId: Number, mappings: ProposalPropertyMapping[]): Promise<boolean> {
    try {
      await httpService.post(
        `/api/Property/CreateRMCClientPropertyMappings/${rmcClientMappingId}/${cityId}`,
        mappings,
        undefined,
        {
          unwrap: true,
          toast: true,
        }
      );
      return true;
    } catch (error) {
      console.error('Failed to create client-property mappings', error);
      return false;
    }
  }

  // Map all properties for a given RMCClientMappingId and CityId
  async mappingAllProperty(rmcClientMappingId: number | string, cityId: number | string): Promise<any> {
    return await httpService.post(
      `/api/Property/MappingAllProperty/${rmcClientMappingId}/${cityId}`,
      undefined,
      undefined,
      {
        unwrap: true,
        toast: true,
      }
    );
  }
  // Add inside PropertyService class

  async createProperty(property: Property): Promise<Property> {
    const formData = new FormData();

    // Required fields
    formData.append("name", property.name);
    formData.append("propertyTypeId", property.propertyTypeId.toString());
    formData.append("cityId", property.cityId.toString());
    formData.append("stateId", property.stateId?.toString() || "0");
    formData.append("countryId", property.countryId?.toString() || "0");
  if (property.vendorId != null) formData.append("vendorId", property.vendorId.toString());
  if (property.tierId != null) formData.append("tierId", property.tierId.toString());
  if (property.pseudoName != null) formData.append("pseudoName", property.pseudoName.toString());

    // Optional fields with proper null handling
    if (property.description) formData.append("description", property.description);
    if (property.address) formData.append("address", property.address);
    if (property.locationId) formData.append("locationId", property.locationId.toString());
    if (property.landmark) formData.append("landmark", property.landmark);
    if (property.zipCode) formData.append("zipCode", property.zipCode);
    //if (property.creditPeriod) formData.append("creditPeriod", property.creditPeriod.toString());
    if (property.totalInventory) formData.append("totalInventory", property.totalInventory.toString());
    if (property.furnishingType) formData.append("furnishingType", property.furnishingType);
    if (property.buildYear) formData.append("buildYear", property.buildYear.toString());
    if (property.brochureLink) formData.append("brochureLink", property.brochureLink);
    if (property.videoLink) formData.append("videoLink", property.videoLink);
    if (property.website) formData.append("website", property.website);
    if (property.masterPlanLink) formData.append("masterPlanLink", property.masterPlanLink);
    if (property.additionalComment) formData.append("additionalComment", property.additionalComment);
    if (property.termsAndConditions) formData.append("termsAndConditions", property.termsAndConditions);
  if (property.vendorId != null && !formData.has("vendorId")) formData.append("vendorId", property.vendorId.toString());
  if (property.tierId != null && !formData.has("tierId")) formData.append("tierId", property.tierId.toString());
  if (property.pseudoName != null && !formData.has("pseudoName")) formData.append("pseudoName", property.pseudoName.toString());

    // Arrays as JSON
    if (property.amenities?.length) formData.append("amenities", JSON.stringify(property.amenities));
    if (property.roomTypes?.length) formData.append("roomTypes", JSON.stringify(property.roomTypes));
    if (property.facilities?.length) formData.append("facilities", JSON.stringify(property.facilities));
    if (property.deleteImageIds?.length) {
      property.deleteImageIds.forEach((id, idx) => formData.append(`DeleteImageIds[${idx}]`, id.toString()));
    }
    if (property.mainImageId) formData.append("MainImageId", property.mainImageId.toString());

    // Handle image files
    if (property.uploadedFiles && property.uploadedFiles.length > 0) {
      for (let i = 0; i < property.uploadedFiles.length; i++) {
        const file = property.uploadedFiles[i];
        
        // Read file as ArrayBuffer to match C# byte[] requirement
        const arrayBuffer = await file.arrayBuffer();
        // Convert ArrayBuffer to base64 string safely
        const uint8Array = new Uint8Array(arrayBuffer);
        const chunks = [];
        for (let i = 0; i < uint8Array.length; i++) {
            chunks.push(String.fromCharCode(uint8Array[i]));
        }
        const base64String = btoa(chunks.join(''));
        
        formData.append(`ImageDtos[${i}].ImageByte`, base64String);
        formData.append(`ImageDtos[${i}].FileName`, file.name);
        formData.append(`ImageDtos[${i}].ContentType`, file.type);
        formData.append(`ImageDtos[${i}].SizeInBytes`, file.size.toString());
        formData.append(`ImageDtos[${i}].IsMainImage`, i === 0 ? "true" : "false");
      }
    }

    const result = await httpService.post<Property>(
      `/api/Property/Create`,
      formData,
  undefined,
      {
        unwrap: true,
        normalize: false,
        toast: true
      }
    );

    return result;
  }

  async updateProperty(id: number, property: Property): Promise<Property> {
    const formData = new FormData();

    formData.append("name", property.name);
    formData.append("propertyTypeId", property.propertyTypeId.toString());
    formData.append("cityId", property.cityId.toString());
    if (property.stateId) formData.append("stateId", property.stateId.toString());
    if (property.countryId) formData.append("countryId", property.countryId.toString());
    if (property.description) formData.append("description", property.description);
  if (property.vendorId != null) formData.append("vendorId", property.vendorId.toString());
    if (property.tierId != null) formData.append("tierId", property.tierId.toString());
  if (property.pseudoName != null) formData.append("pseudoName", property.pseudoName.toString());
    if (property.address) formData.append("address", property.address);
    if (property.locationId) formData.append("locationId", property.locationId.toString());
    if (property.landmark) formData.append("landmark", property.landmark);
    if (property.zipCode) formData.append("zipCode", property.zipCode);
   // if (property.creditPeriod != null) formData.append("creditPeriod", property.creditPeriod.toString());
    if (property.totalInventory != null) formData.append("totalInventory", property.totalInventory.toString());
    if (property.furnishingType) formData.append("furnishingType", property.furnishingType);
    if (property.buildYear != null) formData.append("buildYear", property.buildYear.toString());
    if (property.brochureLink) formData.append("brochureLink", property.brochureLink);
    if (property.videoLink) formData.append("videoLink", property.videoLink);
    if (property.website) formData.append("website", property.website);
    if (property.masterPlanLink) formData.append("masterPlanLink", property.masterPlanLink);
    if (property.additionalComment) formData.append("additionalComment", property.additionalComment);
    if (property.termsAndConditions) formData.append("termsAndConditions", property.termsAndConditions);
    if (property.isActive != null) formData.append("isActive", property.isActive ? "true" : "false");

    if (property.amenities?.length) formData.append("amenities", JSON.stringify(property.amenities));
    if (property.roomTypes?.length) formData.append("roomTypes", JSON.stringify(property.roomTypes));
    if (property.facilities?.length) formData.append("facilities", JSON.stringify(property.facilities));
    if (property.deleteImageIds?.length) {
      property.deleteImageIds.forEach((imgId, idx) => formData.append(`DeleteImageIds[${idx}]`, imgId.toString()));
    }
    if (property.mainImageId) formData.append("MainImageId", property.mainImageId.toString());

    if (property.uploadedFiles && property.uploadedFiles.length > 0) {
      for (let i = 0; i < property.uploadedFiles.length; i++) {
        const file = property.uploadedFiles[i];
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const chunks: string[] = [];
        for (let j = 0; j < uint8Array.length; j++) {
          chunks.push(String.fromCharCode(uint8Array[j]));
        }
        const base64String = btoa(chunks.join(''));
        formData.append(`ImageDtos[${i}].ImageByte`, base64String);
        formData.append(`ImageDtos[${i}].FileName`, file.name);
        formData.append(`ImageDtos[${i}].ContentType`, file.type);
        formData.append(`ImageDtos[${i}].SizeInBytes`, file.size.toString());
        // Determine main logic: priority 1 explicit mainImageId (existing), priority 2 newMainUploadIndex, else first new
        const isMain = property.mainImageId == null && (
          (property.newMainUploadIndex != null && property.newMainUploadIndex === i) ||
          (property.newMainUploadIndex == null && i === 0)
        );
        formData.append(`ImageDtos[${i}].IsMainImage`, isMain ? "true" : "false");
      }
    }

    const result = await httpService.put<Property>(
      `/api/Property/Update/${id}`,
      formData,
      undefined,
      { unwrap: true, normalize: false, toast: true }
    );
    return result;
  }

}

export const propertyService = new PropertyService();